import { useDisclosureContext } from '@vivli/features/disclosures/infrastructure/context';
import { HistoryListView } from '@vivli/shared/views';
import { useMemo } from 'react';
import { IHistory } from '@vivli/shared/infrastructure/interface';
import { IDataPackageFileDescriptor } from '@vivli/shared/features/data-package/infrastructure/interface';
import { useOrgFiles } from '@vivli/features/disclosures/infrastructure/hook';

export const DisclosureHistoryView = () => {
    const { disclosure, attachmentsPackageApi, reviewDocsPackageApi, user, permissions } = useDisclosureContext();

    const attachmentFiles = useOrgFiles(
        attachmentsPackageApi?.dataPackage?.dataPackageFileDescriptors,
        user,
        permissions?.viewAllAttachments
    );
    const reviewFiles = useOrgFiles(reviewDocsPackageApi?.dataPackage?.dataPackageFileDescriptors, user, true);

    const _mapFileToHistory = ({
        file,
        description,
        onClick,
    }: {
        file: IDataPackageFileDescriptor;
        description: string;
        onClick: () => void;
    }) => ({
        userId: file.uploadedByUserId,
        userEmail: file.uploadedByUserEmail,
        userName: file.uploadedByUserName,
        dateTime: new Date(file.updatedDateTime),
        orgName: file.organizationName,
        organizationId: file.organizationId,
        comment: file.name,
        description,
        onClick,
    });

    const historyEntries = useMemo(() => {
        const attachmentsAsHistory =
            attachmentFiles?.map<IHistory>((file) =>
                _mapFileToHistory({
                    file,
                    description: `Feedback attachment uploaded`,
                    onClick: () => attachmentsPackageApi.onFileDownload(file),
                })
            ) || [];

        const reviewFilesAsHistory =
            reviewFiles?.map<IHistory>((file) =>
                _mapFileToHistory({
                    file,
                    description: `Review document uploaded`,
                    onClick: () => reviewDocsPackageApi.onFileDownload(file),
                })
            ) || [];

        const history = disclosure?.disclosureHistories?.map((historyEntry) => ({
            ...historyEntry,
            orgName: historyEntry.organizationName,
        }));

        return [...history, ...attachmentsAsHistory, ...reviewFilesAsHistory];
    }, [
        disclosure?.disclosureHistories,
        attachmentsPackageApi.dataPackage?.dataPackageFileDescriptors,
        reviewDocsPackageApi?.dataPackage?.dataPackageFileDescriptors,
    ]);

    return (
        <HistoryListView
            historyEntries={historyEntries || []}
            includeOrgName={true}
            showExportBtn={true}
            hideFloatingFilter={false}
            exportFileName={`public-disclosure-history-${new Date().toISOString()}.csv`}
        />
    );
};
